/**
 * Product Card Gallery -- Mobile swipe-to-browse images on PLP (task #53)
 *
 * Adds horizontal swipe gesture detection to product cards with multiple images.
 * Only activates on touch devices (hover: none). Desktop keeps the existing
 * CSS hover-swap behaviour.
 *
 * Architecture:
 *   touchstart -> record start X/Y
 *   touchmove  -> track current X/Y, prevent scroll if horizontal swipe detected
 *   touchend   -> if horizontal delta > threshold, navigate gallery
 *   click      -> if swiping, prevent link navigation to PDP
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 30;
  var isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  if (!isTouch) return;

  var startX = 0;
  var startY = 0;
  var currentX = 0;
  var isSwiping = false;
  var swipeTarget = null;
  /* Short-lived flag the click handler reads to suppress PDP navigation after a real swipe.
     `isSwiping` is cleared in touchend before the synthetic click fires, so it can't be reused here. */
  var recentlySwiped = false;

  /**
   * Find the closest gallery container from a touch target.
   * @param {Element} el
   * @returns {Element|null}
   */
  function findGallery(el) {
    return el.closest('.product-card__media[data-gallery-count]');
  }

  /**
   * Get the current active index for a gallery container.
   * @param {Element} gallery
   * @returns {number}
   */
  function getActiveIndex(gallery) {
    var active = gallery.querySelector('.product-card__image--gallery.is-active');
    return active ? parseInt(active.getAttribute('data-gallery-index'), 10) || 0 : 0;
  }

  /**
   * Get the total number of images in a gallery.
   * @param {Element} gallery
   * @returns {number}
   */
  function getGalleryCount(gallery) {
    return parseInt(gallery.getAttribute('data-gallery-count'), 10) || 1;
  }

  /**
   * Navigate to a specific image index in the gallery.
   * @param {Element} gallery
   * @param {number} index
   */
  function navigateTo(gallery, index) {
    var count = getGalleryCount(gallery);
    if (index < 0 || index >= count) return;

    var images = gallery.querySelectorAll('.product-card__image--gallery');
    var segments = gallery.querySelectorAll('.product-card__gallery-segment');

    for (var i = 0; i < images.length; i++) {
      var imgIndex = parseInt(images[i].getAttribute('data-gallery-index'), 10);
      if (imgIndex === index) {
        images[i].classList.add('is-active');
        images[i].setAttribute('aria-hidden', 'false');
      } else {
        images[i].classList.remove('is-active');
        images[i].setAttribute('aria-hidden', 'true');
      }
    }

    for (var j = 0; j < segments.length; j++) {
      var segmentIndex = parseInt(segments[j].getAttribute('data-segment-index'), 10);
      if (segmentIndex === index) {
        segments[j].classList.add('is-active');
      } else {
        segments[j].classList.remove('is-active');
      }
    }
  }

  document.addEventListener('touchstart', function (e) {
    var gallery = findGallery(e.target);
    if (!gallery) return;

    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    currentX = startX;
    isSwiping = false;
    recentlySwiped = false;
    swipeTarget = gallery;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!swipeTarget) return;

    currentX = e.touches[0].clientX;
    var deltaX = Math.abs(currentX - startX);
    var deltaY = Math.abs(e.touches[0].clientY - startY);

    /* If horizontal movement exceeds vertical, this is a swipe -- prevent vertical scroll */
    if (deltaX > deltaY && deltaX > 10) {
      isSwiping = true;
      e.preventDefault();
    }
  }, { passive: false });

  document.addEventListener('touchend', function () {
    if (!swipeTarget || !isSwiping) {
      swipeTarget = null;
      isSwiping = false;
      return;
    }

    var deltaX = currentX - startX;
    var gallery = swipeTarget;

    swipeTarget = null;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) {
      isSwiping = false;
      return;
    }

    /* A real swipe happened - flag it so the synthetic click that follows is suppressed */
    recentlySwiped = true;

    var activeIndex = getActiveIndex(gallery);
    var count = getGalleryCount(gallery);

    if (deltaX < 0) {
      /* Swipe left -> next image */
      if (activeIndex < count - 1) {
        navigateTo(gallery, activeIndex + 1);
      }
    } else {
      /* Swipe right -> previous image */
      if (activeIndex > 0) {
        navigateTo(gallery, activeIndex - 1);
      }
    }

    isSwiping = false;
  }, { passive: true });

  /* Prevent link navigation after a swipe (consume the flag so later genuine taps pass through) */
  document.addEventListener('click', function (e) {
    if (!recentlySwiped) return;
    recentlySwiped = false;

    var gallery = findGallery(e.target);
    if (gallery) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);
})();
